import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';

const btnRunSchedule =
  'rounded-full bg-evault-teal-deep px-3.5 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-evault-teal-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-evault-teal/40 focus-visible:ring-offset-2 sm:text-sm';

const btnSchedule =
  'rounded-full bg-evault-navy px-3.5 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-evault-navy/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-evault-navy/40 focus-visible:ring-offset-2 sm:text-sm';

const btnExport =
  'rounded-full bg-evault-gold px-3.5 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-evault-gold-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-evault-gold/40 focus-visible:ring-offset-2 sm:text-sm';

const filterSelect =
  'rounded-full border border-evault-select-ring/80 bg-white px-3 py-2 text-xs text-umb-neutral-dark shadow-sm focus:border-evault-teal focus:outline-none focus:ring-2 focus:ring-evault-teal/20 sm:text-sm';

/** @typedef {{ id: string, label: string, options: string[] }} FilterField */
/** @typedef {{ label: string, value: string, valueClass: string, hint?: string }} Kpi */
/** @typedef {{ label: string, pct: number, barClass: string }} CompositionRow */
/** @typedef {{ values: string[], valueClasses?: (string | null | undefined)[] }} DetailRow */

/**
 * Full dashboard shell — same structure as Boarding Summary for every report module.
 * @type {Record<string, {
 *   dashboardTitle: string,
 *   dashboardSubtitle: string,
 *   filterFields: FilterField[],
 *   kpis: Kpi[],
 *   composition: CompositionRow[],
 *   compositionSectionTitle?: string,
 *   detailColumns: string[],
 *   detailRows: DetailRow[],
 *   bodyVariant?: 'intake-throughput' | 'authority-outcomes' | 'custody-handoff-wireframe' | 'integration-health-wireframe' | 'custody-movement' | 'custody-trend-breakdown' | 'certification-receipts-dashboard' | 'turnaround-sla-dashboard',
 * }>}
 */
const FULL_REPORT_CONFIG = {
  '/reports/boarding': {
    dashboardTitle: 'Boarding Summary Dashboard',
    dashboardSubtitle:
      'Dashboard filters, metrics, charts, table, then action controls.',
    filterFields: [
      { id: 'program', label: 'Program', options: ['All programs', 'Equipment FL', 'CRE Master 2026'] },
      { id: 'daterange', label: 'Date range', options: ['Last 30 days', 'Quarter', 'YTD'] },
      { id: 'submitter', label: 'Submitter', options: ['All submitters', 'Originator API', 'Portal'] },
      { id: 'stage', label: 'Stage', options: ['All stages', 'Pre-validation', 'Custody review'] },
      { id: 'channel', label: 'Channel', options: ['All channels', 'API', 'SFTP', 'Portal'] },
    ],
    kpis: [
      { label: 'Packages received', value: '214', valueClass: 'text-evault-navy' },
      { label: 'Files boarded', value: '8,420', valueClass: 'text-evault-teal-deep' },
      { label: 'Open exceptions', value: '37', valueClass: 'text-evault-gold' },
      { label: 'Overdue cures', value: '9', valueClass: 'text-red-600' },
      { label: 'Median TAT', value: '5.2d', valueClass: 'text-evault-vault-active' },
    ],
    composition: [
      { label: 'Received', pct: 100, barClass: 'bg-evault-teal-deep' },
      { label: 'Prevalidated', pct: 78, barClass: 'bg-evault-navy' },
      { label: 'Review required', pct: 42, barClass: 'bg-evault-gold' },
      { label: 'Custody review', pct: 28, barClass: 'bg-evault-chart-teal-soft' },
      { label: 'Rejected', pct: 8, barClass: 'bg-red-500' },
    ],
    detailColumns: ['Batch', 'Program', 'Files', 'Open issues', 'Current status'],
    detailRows: [
      {
        values: ['B-10482', 'Equipment FL', '112', '2', 'Custody review'],
        valueClasses: [null, null, null, 'font-semibold text-evault-gold', 'font-medium text-evault-teal-deep'],
      },
      {
        values: ['B-10471', 'CRE Master 2026', '64', '0', 'Prevalidated'],
        valueClasses: [null, null, null, 'text-evault-muted', 'font-medium text-evault-vault-active'],
      },
      {
        values: ['B-10465', 'ABS Auto', '210', '5', 'Review required'],
        valueClasses: [null, null, null, 'font-semibold text-evault-gold', 'font-medium text-evault-gold'],
      },
    ],
  },

  '/reports/authority': {
    dashboardTitle: 'Authority Outcomes Dashboard',
    dashboardSubtitle:
      'Authority funnel — candidates, admission, repository-only, rejections, and custodian queue.',
    filterFields: [
      { id: 'program', label: 'Program', options: ['All programs', 'CRE Master 2026', 'ABS Auto'] },
      { id: 'daterange', label: 'Date range', options: ['Last 30 days', 'Quarter', 'YTD'] },
      { id: 'outcome', label: 'Outcome', options: ['All outcomes', 'Admitted', 'Repository only', 'Rejected'] },
      { id: 'custodian', label: 'Custodian', options: ['All custodians', 'Custodian', 'Other'] },
      { id: 'package', label: 'Package', options: ['All packages', 'pkg-889', 'pkg-902'] },
    ],
    kpis: [
      { label: 'Authority candidates', value: '64', valueClass: 'text-evault-navy' },
      { label: 'Admitted', value: '51', valueClass: 'text-evault-vault-active' },
      { label: 'Repository only', value: '9', valueClass: 'text-evault-teal-deep' },
      { label: 'Rejected', value: '3', valueClass: 'text-red-600' },
      { label: 'Pending custodian', value: '12', valueClass: 'text-evault-gold' },
    ],
    composition: [
      { label: 'Admitted', pct: 80, barClass: 'bg-evault-vault-active' },
      { label: 'Repository only', pct: 45, barClass: 'bg-evault-teal-deep' },
      { label: 'Pending review', pct: 38, barClass: 'bg-evault-gold' },
      { label: 'Awaiting admission', pct: 22, barClass: 'bg-evault-navy' },
      { label: 'Rejected', pct: 12, barClass: 'bg-red-500' },
    ],
    compositionSectionTitle: 'Outcome composition',
    detailColumns: ['Asset', 'Outcome', 'Custodian', 'Date'],
    detailRows: [
      {
        values: ['LN-44102', 'Admitted', 'Custodian', 'Mar 12'],
        valueClasses: [null, 'font-semibold text-evault-vault-active', null, null],
      },
      {
        values: ['LN-44088', 'Repository only', 'Custodian', 'Mar 11'],
        valueClasses: [null, 'font-medium text-evault-teal-deep', null, null],
      },
      {
        values: ['LN-44071', 'Pending review', 'Custodian', 'Mar 10'],
        valueClasses: [null, 'font-semibold text-evault-gold', null, null],
      },
      {
        values: ['LN-44012', 'Rejected', 'Custodian', 'Mar 08'],
        valueClasses: [null, 'font-semibold text-red-600', null, null],
      },
    ],
  },

  '/reports/custody': {
    dashboardTitle: 'Custody / Facility Status Dashboard',
    dashboardSubtitle:
      'Vault, pledged, release pending, transfers, and facility-tied positions in one view.',
    filterFields: [
      { id: 'facility', label: 'Facility / SPV', options: ['All facilities', 'BR-SPV-I', 'Warehouse A'] },
      { id: 'daterange', label: 'Date range', options: ['Last 30 days', 'Quarter', 'YTD'] },
      { id: 'state', label: 'Custody state', options: ['All states', 'In vault', 'Pledged', 'Released'] },
      { id: 'program', label: 'Program', options: ['All programs', 'CRE Master 2026', 'Equipment FL'] },
      { id: 'channel', label: 'Channel', options: ['All channels', 'API', 'Portal'] },
    ],
    kpis: [
      { label: 'In vault', value: '128', valueClass: 'text-evault-teal-deep' },
      { label: 'Pledged', value: '34', valueClass: 'text-evault-navy' },
      { label: 'Release pending', value: '5', valueClass: 'text-evault-gold' },
      { label: 'Transferred out', value: '18', valueClass: 'text-evault-transferred' },
      { label: 'Repo / unpledged', value: '41', valueClass: 'text-evault-muted' },
    ],
    composition: [
      { label: 'In vault', pct: 100, barClass: 'bg-evault-teal-deep' },
      { label: 'Pledged', pct: 52, barClass: 'bg-evault-navy' },
      { label: 'Release pending', pct: 18, barClass: 'bg-evault-gold' },
      { label: 'Transferred', pct: 35, barClass: 'bg-evault-transferred' },
      { label: 'Repository only', pct: 62, barClass: 'bg-evault-chart-teal-soft' },
    ],
    compositionSectionTitle: 'Position composition',
    detailColumns: ['Asset', 'State', 'Facility', 'Updated'],
    detailRows: [
      {
        values: ['LN-44102', 'Pledged', 'BR-SPV-I', 'Mar 18'],
        valueClasses: [null, 'font-semibold text-evault-navy', null, null],
      },
      {
        values: ['LN-44012', 'In vault', '—', 'Mar 17'],
        valueClasses: [null, 'font-medium text-evault-teal-deep', 'text-evault-muted', null],
      },
      {
        values: ['LN-43988', 'Release pending', 'Warehouse A', 'Mar 16'],
        valueClasses: [null, 'font-semibold text-evault-gold', null, null],
      },
      {
        values: ['LN-43901', 'Transferred', 'East SPV II', 'Mar 14'],
        valueClasses: [null, 'font-medium text-evault-transferred', null, null],
      },
    ],
  },

  '/reports/spv-servicing': {
    dashboardTitle: 'SPV / Sold with Servicing Dashboard',
    dashboardSubtitle:
      'Loans no longer on-book but still visible — servicing retained and access basis.',
    filterFields: [
      { id: 'buyer', label: 'Buyer / SPV', options: ['All buyers', 'East SPV II', 'River Trust'] },
      { id: 'daterange', label: 'Date range', options: ['Last 30 days', 'Quarter', 'YTD'] },
      { id: 'servicing', label: 'Servicing', options: ['All', 'Retained', 'Released'] },
      { id: 'basis', label: 'Access basis', options: ['All bases', 'Servicer', 'Consent', 'Contract'] },
      { id: 'program', label: 'Program', options: ['All programs', 'ABS Auto', 'CRE Master 2026'] },
    ],
    kpis: [
      { label: 'Sold / transferred', value: '22', valueClass: 'text-evault-navy' },
      { label: 'Servicing retained', value: '22', valueClass: 'text-evault-teal-deep' },
      { label: 'Evidence current', value: '19', valueClass: 'text-evault-vault-active' },
      { label: 'Access review due', value: '4', valueClass: 'text-evault-gold' },
      { label: 'Residual flags', value: '1', valueClass: 'text-red-600' },
    ],
    composition: [
      { label: 'Servicing retained', pct: 100, barClass: 'bg-evault-teal-deep' },
      { label: 'Evidence complete', pct: 86, barClass: 'bg-evault-vault-active' },
      { label: 'Buyer reporting', pct: 64, barClass: 'bg-evault-navy' },
      { label: 'Consent / legal hold', pct: 28, barClass: 'bg-evault-gold' },
      { label: 'Access lapsing', pct: 9, barClass: 'bg-red-500' },
    ],
    compositionSectionTitle: 'Servicing visibility mix',
    detailColumns: ['Loan', 'Buyer / SPV', 'Servicing', 'Access basis'],
    detailRows: [
      {
        values: ['LN-33901', 'East SPV II', 'Retained', 'Servicer'],
        valueClasses: [null, null, 'font-semibold text-evault-teal-deep', 'text-umb-neutral-dark/85'],
      },
      {
        values: ['LN-33812', 'River Trust', 'Retained', 'Consent'],
        valueClasses: [null, null, 'font-semibold text-evault-teal-deep', null],
      },
      {
        values: ['LN-33790', 'Maple ABS', 'Retained', 'Contract'],
        valueClasses: [null, null, 'font-semibold text-evault-teal-deep', null],
      },
      {
        values: ['LN-33650', 'Harbor SPV', 'Released', '—'],
        valueClasses: [null, null, 'text-evault-muted', 'text-evault-muted'],
      },
    ],
  },

  '/reports/evidence': {
    dashboardTitle: 'Evidence & Export History Dashboard',
    dashboardSubtitle:
      'Bundles, receipts, certified copies, and report exports — status and lineage.',
    filterFields: [
      { id: 'type', label: 'Artifact type', options: ['All types', 'Bundle', 'Receipt', 'Export'] },
      { id: 'daterange', label: 'Date range', options: ['Last 30 days', 'Quarter', 'YTD'] },
      { id: 'status', label: 'Status', options: ['All statuses', 'Sealed', 'Issued', 'Pending'] },
      { id: 'program', label: 'Program', options: ['All programs', 'CRE Master 2026', 'Equipment FL'] },
      { id: 'channel', label: 'Channel', options: ['All channels', 'API', 'Portal', 'Custodian'] },
    ],
    kpis: [
      { label: 'Evidence bundles', value: '210', valueClass: 'text-evault-teal-deep' },
      { label: 'Custody receipts', value: '198', valueClass: 'text-evault-navy' },
      { label: 'Report exports (30d)', value: '36', valueClass: 'text-evault-gold' },
      { label: 'Pending seal', value: '7', valueClass: 'text-red-600' },
      { label: 'Certified copies', value: '124', valueClass: 'text-evault-vault-active' },
    ],
    composition: [
      { label: 'Evidence bundles', pct: 100, barClass: 'bg-evault-teal-deep' },
      { label: 'Custody receipts', pct: 94, barClass: 'bg-evault-navy' },
      { label: 'Certified copies', pct: 59, barClass: 'bg-evault-vault-active' },
      { label: 'Report exports', pct: 33, barClass: 'bg-evault-gold' },
      { label: 'Failed / quarantined', pct: 4, barClass: 'bg-red-500' },
    ],
    compositionSectionTitle: 'Artifact mix',
    detailColumns: ['Artifact', 'Type', 'Status', 'Created'],
    detailRows: [
      {
        values: ['EV-8821', 'Evidence bundle', 'Sealed', 'Mar 10'],
        valueClasses: [null, null, 'font-semibold text-evault-vault-active', null],
      },
      {
        values: ['RC-4401', 'Custody receipt', 'Issued', 'Mar 09'],
        valueClasses: [null, null, 'font-medium text-evault-teal-deep', null],
      },
      {
        values: ['CC-2203', 'Certified copy', 'Available', 'Mar 08'],
        valueClasses: [null, null, 'font-medium text-evault-navy', null],
      },
      {
        values: ['XP-991', 'Report export', 'Pending', 'Mar 07'],
        valueClasses: [null, null, 'font-semibold text-evault-gold', null],
      },
    ],
  },
};

/** Provider workspace dashboards — wireframes v2 */
const PROVIDER_REPORT_CONFIG = {
  '/provider/reports/intake-throughput': {
    bodyVariant: 'intake-throughput',
    dashboardTitle: 'Intake Throughput Dashboard',
    dashboardSubtitle:
      'Operational reporting on volume, latency, queue aging, and source concentration.',
    filterFields: [
      { id: 'program', label: 'Program', options: ['All', 'CRE Master 2026', 'ABS Auto', 'Equipment FL'] },
      { id: 'submitter', label: 'Submitter', options: ['All', 'Originator API', 'Portal', 'SFTP partners'] },
      { id: 'daterange', label: 'Date', options: ['Last 30 days', 'Last 7 days', 'Quarter', 'YTD'] },
      { id: 'stage', label: 'Stage', options: ['All', 'Received', 'Validated', 'Review required'] },
    ],
    kpis: [
      {
        label: 'Batches received',
        value: '842',
        valueClass: 'text-evault-teal-deep',
        hint: 'avg / day 28',
      },
      {
        label: 'Median validate time',
        value: '11.4',
        valueClass: 'text-evault-teal-deep',
        hint: 'minutes',
      },
      {
        label: 'Review-required rate',
        value: '18%',
        valueClass: 'text-evault-gold',
        hint: 'of batches',
      },
      {
        label: 'Quarantine rate',
        value: '2.3%',
        valueClass: 'text-red-600',
        hint: 'technical rejects',
      },
    ],
    composition: [],
    detailColumns: [],
    detailRows: [],
  },

  '/provider/reports/authority-outcomes': {
    bodyVariant: 'authority-outcomes',
    dashboardTitle: 'Authority Outcomes Dashboard',
    dashboardSubtitle:
      'Visibility into candidate, repository-only, duplicate-block, and admit (custodian-routed) outcomes.',
    filterFields: [
      { id: 'program', label: 'Program', options: ['All', 'CRE Master 2026', 'ABS Auto', 'Equipment FL'] },
      { id: 'submitter', label: 'Submitter', options: ['All', 'Originator API', 'Portal', 'SFTP'] },
      { id: 'daterange', label: 'Date', options: ['Last 30 days', 'Quarter', 'YTD'] },
      { id: 'outcome', label: 'Outcome', options: ['All', 'Candidate', 'Repository only', 'Blocked', 'Custodian routed'] },
    ],
    kpis: [
      {
        label: 'Candidates',
        value: '391',
        valueClass: 'text-evault-vault-active',
        hint: 'current period',
      },
      {
        label: 'Repository only',
        value: '212',
        valueClass: 'text-umb-neutral-dark',
        hint: 'non-authoritative',
      },
      {
        label: 'Duplicate blocked',
        value: '14',
        valueClass: 'text-red-600',
        hint: 'conflict cases',
      },
      {
        label: 'Custodian routed',
        value: '156',
        valueClass: 'text-evault-teal-deep',
        hint: 'authority ready (admit path)',
      },
    ],
    composition: [],
    detailColumns: [],
    detailRows: [],
  },

  '/provider/reports/custody-handoff': {
    bodyVariant: 'custody-handoff-wireframe',
    dashboardTitle: 'Custody Packaging & Handoff Dashboard',
    dashboardSubtitle:
      'Detailed reporting for packaged actions — ready for custodian, delayed, executed, and rejected or returned items.',
    filterFields: [],
    kpis: [
      {
        label: 'Ready to route',
        value: '44',
        valueClass: 'text-evault-vault-active',
        hint: 'all actions',
      },
      {
        label: 'Awaiting custodian',
        value: '27',
        valueClass: 'text-evault-teal-deep',
        hint: 'open SLAs',
      },
      {
        label: 'Rejected / returned',
        value: '6',
        valueClass: 'text-red-600',
        hint: 'need repackaging',
      },
      {
        label: 'Executed',
        value: '118',
        valueClass: 'text-evault-navy',
        hint: 'this period',
      },
    ],
    composition: [],
    detailColumns: [],
    detailRows: [],
  },

  '/provider/reports/integration-health': {
    bodyVariant: 'integration-health-wireframe',
    dashboardTitle: 'Integration Health Dashboard',
    dashboardSubtitle:
      'Detailed reporting dashboard for API, webhook, SFTP, and partner feed health.',
    filterFields: [],
    kpis: [
      {
        label: 'API success rate',
        value: '99.4%',
        valueClass: 'text-evault-vault-active',
        hint: 'last 24h',
      },
      {
        label: 'Webhook failure rate',
        value: '1.8%',
        valueClass: 'text-red-600',
        hint: '4 active fails',
      },
      {
        label: 'SFTP backlog',
        value: '8',
        valueClass: 'text-evault-gold',
        hint: 'files pending',
      },
      {
        label: 'Partner sync lag',
        value: '2m',
        valueClass: 'text-evault-teal-deep',
        hint: 'median',
      },
    ],
    composition: [],
    detailColumns: [],
    detailRows: [],
  },
};

/** Document custodian workspace dashboards — wireframes v2 */
const CUSTODIAN_REPORT_CONFIG = {
  '/custodian/reports/inventory': {
    bodyVariant: 'custody-trend-breakdown',
    dashboardTitle: 'Custody Inventory Dashboard',
    dashboardSubtitle:
      'State and inventory mix: active, pledged, released, transferred, and repository populations.',
    filterFields: [
      { id: 'facility', label: 'Facility / SPV', options: ['All', 'BR-SPV-I', 'Auto-WH-2', 'East SPV II'] },
      { id: 'state', label: 'Custody state', options: ['All', 'Authoritative active', 'Pledged', 'Released', 'Transferred', 'Repository only'] },
      { id: 'program', label: 'Program', options: ['All programs', 'CRE Master 2026', 'ABS Auto'] },
      { id: 'daterange', label: 'Date range', options: ['Last 30 days', 'Quarter', 'YTD'] },
      { id: 'receipt', label: 'Receipt status', options: ['All', 'Issued', 'Pending', 'Superseded'] },
    ],
    kpis: [
      { label: 'Under custody view', value: '226', valueClass: 'text-evault-teal-deep' },
      { label: 'Authoritative active', value: '128', valueClass: 'text-evault-vault-active' },
      { label: 'Pledged', value: '58', valueClass: 'text-evault-navy' },
      { label: 'Release / transfer pending', value: '11', valueClass: 'text-evault-gold' },
      { label: 'Repository only', value: '41', valueClass: 'text-evault-muted' },
    ],
    composition: [
      { label: 'Authoritative active', pct: 100, barClass: 'bg-evault-vault-active' },
      { label: 'Pledged', pct: 45, barClass: 'bg-evault-navy' },
      { label: 'Released', pct: 22, barClass: 'bg-evault-chart-teal-soft' },
      { label: 'Transferred', pct: 18, barClass: 'bg-evault-transferred' },
      { label: 'Repository only', pct: 32, barClass: 'bg-evault-gold' },
    ],
    compositionSectionTitle: 'Custody population mix',
    detailColumns: ['Asset', 'State', 'Facility', 'Receipt', 'Updated'],
    detailRows: [
      {
        values: ['LN-44102', 'Authoritative active', 'BR-SPV-I', 'RC-8821', 'Mar 21'],
        valueClasses: [null, 'font-medium text-evault-vault-active', null, null, null],
      },
      {
        values: ['LN-44012', 'Pledged', 'BR-SPV-I', 'RC-8804', 'Mar 19'],
        valueClasses: [null, 'font-semibold text-evault-navy', null, null, null],
      },
      {
        values: ['LN-44118', 'Repository only', 'Auto-WH-2', '—', 'Mar 18'],
        valueClasses: [null, 'text-evault-muted', null, 'text-evault-muted', null],
      },
    ],
  },

  '/custodian/reports/movement': {
    bodyVariant: 'custody-movement',
    dashboardTitle: 'Custody Movement Dashboard',
    dashboardSubtitle:
      'Report dashboard for lifecycle actions — pledges, releases, transfers, and certified copies (30d view).',
    filterFields: [
      { id: 'facility', label: 'Facility / SPV', options: ['All', 'BR-SPV-I', 'Auto-WH-2', 'Warehouse A'] },
      { id: 'daterange', label: 'Date range', options: ['Last 30 days', 'Quarter', 'YTD'] },
      { id: 'action', label: 'Action type', options: ['All', 'Pledge', 'Release', 'Transfer', 'Certified copy'] },
      { id: 'outcome', label: 'Outcome', options: ['All', 'Executed', 'Rejected', 'Withdrawn'] },
    ],
    kpis: [
      { label: 'Pledges', value: '96', valueClass: 'text-evault-gold', hint: '30d' },
      { label: 'Releases', value: '41', valueClass: 'text-evault-teal-deep', hint: '30d' },
      { label: 'Transfers', value: '27', valueClass: 'text-evault-teal-deep', hint: '30d' },
      { label: 'Copies', value: '58', valueClass: 'text-evault-vault-active', hint: '30d' },
    ],
    composition: [],
    detailColumns: [],
    detailRows: [],
  },

  '/custodian/reports/certification-receipts': {
    bodyVariant: 'certification-receipts-dashboard',
    dashboardTitle: 'Certification & Receipts Dashboard',
    dashboardSubtitle:
      'Certificates, receipts, and delivery outcomes — recent activity and issuance posture.',
    filterFields: [
      { id: 'artifact', label: 'Artifact', options: ['All', 'Receipt', 'Certificate', 'Certified copy'] },
      { id: 'state', label: 'State', options: ['All', 'Issued', 'Pending', 'Superseded'] },
      { id: 'delivery', label: 'Delivery', options: ['All', 'Delivered', 'Failed', 'Packaging'] },
      { id: 'daterange', label: 'Date range', options: ['Last 30 days', 'Quarter'] },
      { id: 'facility', label: 'Facility', options: ['All', 'UMB Line A', 'SPV 2026-1'] },
    ],
    kpis: [
      {
        label: 'Certificates issued',
        value: '87',
        valueClass: 'text-evault-vault-active',
        hint: '30d',
      },
      {
        label: 'Pending issue',
        value: '14',
        valueClass: 'font-semibold text-amber-600',
        hint: 'action needed',
      },
      {
        label: 'Superseded',
        value: '6',
        valueClass: 'text-evault-navy',
        hint: 'history',
      },
      {
        label: 'Delivery failures',
        value: '2',
        valueClass: 'text-red-600',
        hint: 'retry',
      },
    ],
    composition: [],
    detailColumns: [
      'Receipt / cert',
      'Asset',
      'Type',
      'State',
      'Issued to',
      'Date',
      'Delivery',
    ],
    detailRows: [
      {
        values: ['RC-22190', 'A-18802', 'Admission', 'Issued', 'Originator', '03/08', 'Delivered'],
        valueClasses: [
          null,
          null,
          null,
          'font-medium text-evault-vault-active',
          null,
          null,
          null,
        ],
      },
      {
        values: ['PL-11807', 'A-18788', 'Pledge', 'Pending', 'UMB Line A', '03/07', 'Packaging'],
        valueClasses: [
          null,
          null,
          null,
          'font-semibold text-evault-gold',
          null,
          null,
          'text-evault-muted',
        ],
      },
      {
        values: ['CC-33218', 'A-18802', 'Copy', 'Issued', 'SPV 2026-1', '03/07', 'Delivered'],
        valueClasses: [
          null,
          null,
          null,
          'font-medium text-evault-vault-active',
          null,
          null,
          null,
        ],
      },
      {
        values: ['TR-8841', 'A-18765', 'Transfer', 'Issued', 'UMB Line A', '03/05', 'Delivered'],
        valueClasses: [
          null,
          null,
          null,
          'font-medium text-evault-vault-active',
          null,
          null,
          null,
        ],
      },
      {
        values: ['RC-22102', 'A-18640', 'Admission', 'Pending', 'Originator', '03/05', 'Packaging'],
        valueClasses: [
          null,
          null,
          null,
          'font-semibold text-evault-gold',
          null,
          null,
          'text-evault-muted',
        ],
      },
    ],
  },

  '/custodian/reports/turnaround-sla': {
    bodyVariant: 'turnaround-sla-dashboard',
    dashboardTitle: 'Turnaround & SLA Dashboard',
    dashboardSubtitle:
      'Queue aging and turnaround — median times by queue and longest open items.',
    filterFields: [
      { id: 'queue', label: 'Queue', options: ['All', 'Awaiting review', 'Pledge', 'Release & copy', 'Clarifications'] },
      { id: 'sla', label: 'SLA tier', options: ['All', 'Standard', 'Expedited'] },
      { id: 'daterange', label: 'Date range', options: ['Last 7 days', 'Last 30 days'] },
      { id: 'team', label: 'Team', options: ['All', 'Custody ops A', 'Custody ops B'] },
      { id: 'breach', label: 'Breach', options: ['All', 'Breached', 'At risk', 'On track'] },
    ],
    kpis: [
      {
        label: 'Median review time',
        value: '3.2 h',
        valueClass: 'text-evault-teal-deep',
        hint: '30d',
      },
      {
        label: 'Median pledge time',
        value: '5.7 h',
        valueClass: 'text-evault-gold',
        hint: '30d',
      },
      {
        label: 'Median release time',
        value: '4.1 h',
        valueClass: 'text-evault-teal-deep',
        hint: '30d',
      },
      {
        label: 'Overdue items',
        value: '9',
        valueClass: 'font-semibold text-red-800',
        hint: 'today',
      },
    ],
    composition: [],
    /** @type {{ label: string, overdue: number, barClass: string }[]} */
    slaQueueAging: [
      { label: 'Awaiting review', overdue: 6, barClass: 'bg-evault-teal-deep' },
      { label: 'Pledge queue', overdue: 3, barClass: 'bg-evault-gold' },
      { label: 'Release / copy', overdue: 4, barClass: 'bg-evault-teal-deep' },
      { label: 'Clarifications', overdue: 2, barClass: 'bg-red-800' },
    ],
    detailColumns: ['Asset', 'Queue', 'Age', 'Blocked on'],
    detailRows: [
      {
        values: ['A-18797', 'Review', '28 h', 'Clarification from provider'],
        valueClasses: [null, null, 'font-semibold text-evault-gold', null],
      },
      {
        values: ['A-18697', 'Release', '19 h', 'Originator payoff letter'],
        valueClasses: [null, null, 'font-semibold text-evault-gold', null],
      },
      {
        values: ['A-18844', 'Pledge', '16 h', 'Counterparty ack'],
        valueClasses: [null, null, 'text-evault-teal-deep', null],
      },
    ],
  },
};

const ALL_REPORT_CONFIG = {
  ...FULL_REPORT_CONFIG,
  ...PROVIDER_REPORT_CONFIG,
  ...CUSTODIAN_REPORT_CONFIG,
};

const throughputPoints = [
  [56, 98],
  [112, 88],
  [168, 82],
  [224, 68],
  [280, 52],
  [336, 44],
  [372, 38],
];

/** Wireframe: teal throughput trend, simple axes */
function ThroughputByDayChart() {
  const poly = throughputPoints.map(([x, y]) => `${x},${y}`).join(' ');
  return (
    <div className="mt-4 rounded-lg bg-white/80 p-3 ring-1 ring-evault-border">
      <svg
        viewBox="0 0 400 140"
        className="h-48 w-full"
        role="img"
        aria-label="Throughput by day — upward trend (mock)"
      >
        <line
          x1="48"
          y1="16"
          x2="48"
          y2="118"
          className="stroke-umb-neutral-mid"
          strokeWidth="1"
        />
        <line
          x1="48"
          y1="118"
          x2="384"
          y2="118"
          className="stroke-umb-neutral-mid"
          strokeWidth="1"
        />
        <polyline
          fill="none"
          className="stroke-evault-teal-deep"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          points={poly}
        />
        {throughputPoints.map(([x, y]) => (
          <circle key={`${x}-${y}`} cx={x} cy={y} r="4" className="fill-evault-teal-deep" />
        ))}
      </svg>
      <p className="mt-1 text-center text-[10px] text-evault-muted">
        Mock series — volume by day
      </p>
    </div>
  );
}

const intakeAgeingBuckets = [
  { bucket: '< 2h', count: '18', owner: 'Provider' },
  { bucket: '2–8h', count: '11', owner: 'Provider' },
  { bucket: '> 8h', count: '6', owner: 'Submitter' },
  { bucket: '> 24h', count: '2', owner: 'Escalation' },
];

function IntakeThroughputWireframeBody() {
  return (
    <div className="grid gap-5 p-5 lg:grid-cols-2 lg:items-stretch lg:gap-6 lg:p-6">
      <div className="rounded-evault border border-evault-border bg-evault-surface/30 p-4 sm:p-5">
        <h3 className="text-sm font-semibold text-umb-neutral-dark">
          Throughput by day
        </h3>
        <p className="mt-1 text-xs text-evault-muted">
          Daily intake volume for the selected filters — source concentration via
          Submitter filter above.
        </p>
        <ThroughputByDayChart />
      </div>
      <div className="overflow-hidden rounded-evault border border-evault-border bg-white shadow-sm">
        <div className="border-b border-evault-border bg-evault-surface/50 px-4 py-3">
          <h3 className="text-sm font-semibold text-umb-neutral-dark">
            Queue aging and top issue sources
          </h3>
          <p className="mt-1 text-[11px] font-medium text-umb-neutral-dark/70">
            Ageing buckets
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[320px] border-collapse text-left text-xs sm:text-sm">
            <thead>
              <tr className="border-b border-evault-border bg-evault-surface/80">
                <th className="px-4 py-2.5 font-semibold text-evault-muted">
                  Bucket
                </th>
                <th className="px-4 py-2.5 font-semibold text-evault-muted">
                  Count
                </th>
                <th className="px-4 py-2.5 font-semibold text-evault-muted">
                  Primary owner
                </th>
              </tr>
            </thead>
            <tbody>
              {intakeAgeingBuckets.map((row, ri) => (
                <tr
                  key={row.bucket}
                  className={`border-b border-evault-border last:border-b-0 ${
                    ri % 2 === 1 ? 'bg-evault-surface/35' : ''
                  }`}
                >
                  <td className="px-4 py-2.5 font-medium text-umb-neutral-dark">
                    {row.bucket}
                  </td>
                  <td className="px-4 py-2.5 tabular-nums text-umb-neutral-dark/90">
                    {row.count}
                  </td>
                  <td className="px-4 py-2.5 text-umb-neutral-dark/85">
                    {row.owner}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/** Segments as % of bar width — matches wireframe outcome mix proportions */
const authorityOutcomeMix = [
  { label: 'Candidate', pct: 46, barClass: 'bg-evault-vault-active' },
  { label: 'Repository', pct: 30, barClass: 'bg-umb-neutral-mid' },
  { label: 'Blocked', pct: 8, barClass: 'bg-red-500' },
  { label: 'Needs provider', pct: 16, barClass: 'bg-evault-gold' },
];

const authorityBlockers = [
  'Missing signature artifacts',
  'Conflicts with existing authoritative asset',
  'Low-confidence legal class unresolved',
  'Missing mandatory clause / metadata',
  'Package completeness rule failed',
];

/** Wireframe: custody movement — upward trend with variation */
const movementTrendPoints = [
  [52, 102],
  [88, 96],
  [124, 100],
  [160, 88],
  [196, 92],
  [232, 84],
  [268, 78],
  [304, 82],
  [340, 70],
  [376, 64],
];

function MovementTrendChart() {
  const poly = movementTrendPoints.map(([x, y]) => `${x},${y}`).join(' ');
  return (
    <div className="mt-4 rounded-lg bg-white/80 p-3 ring-1 ring-evault-border">
      <svg
        viewBox="0 0 400 140"
        className="h-48 w-full"
        role="img"
        aria-label="Movement trend — upward with fluctuations (mock)"
      >
        <line
          x1="40"
          y1="12"
          x2="40"
          y2="122"
          className="stroke-umb-neutral-mid"
          strokeWidth="1"
        />
        <line
          x1="40"
          y1="122"
          x2="388"
          y2="122"
          className="stroke-umb-neutral-mid"
          strokeWidth="1"
        />
        <polyline
          fill="none"
          className="stroke-evault-teal-deep"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          points={poly}
        />
        {movementTrendPoints.map(([x, y]) => (
          <circle key={`${x}-${y}`} cx={x} cy={y} r="4" className="fill-evault-teal-deep" />
        ))}
      </svg>
      <p className="mt-1 text-center text-[10px] text-evault-muted">
        Mock series — movement volume by period
      </p>
    </div>
  );
}

const movementBreakdownRows = [
  {
    label: 'Pledge executed',
    value: 96,
    barClass: 'bg-evault-vault-active',
  },
  {
    label: 'Release executed',
    value: 41,
    barClass: 'bg-evault-navy',
  },
  {
    label: 'Transfer executed',
    value: 27,
    barClass: 'bg-evault-teal-deep',
  },
  {
    label: 'Rejected / withdrawn',
    value: 13,
    barClass: 'bg-red-800',
  },
];

const movementBreakdownMax = 96;

/** Generic trend + composition bars for custodian inventory / certs / SLA dashboards */
function CustodyTrendAndBreakdownBody({
  trendTitle,
  trendSubtitle,
  breakdownTitle,
  breakdownSubtitle,
  composition,
}) {
  return (
    <div className="grid gap-5 p-5 lg:grid-cols-2 lg:items-stretch lg:gap-6 lg:p-6">
      <div className="rounded-evault border border-evault-border bg-evault-surface/30 p-4 sm:p-5">
        <h3 className="text-sm font-semibold text-umb-neutral-dark">{trendTitle}</h3>
        <p className="mt-1 text-xs text-evault-muted">{trendSubtitle}</p>
        <MovementTrendChart />
      </div>
      <div className="rounded-evault border border-evault-border bg-white p-4 shadow-sm sm:p-5">
        <h3 className="text-sm font-semibold text-umb-neutral-dark">{breakdownTitle}</h3>
        <p className="mt-1 text-xs text-evault-muted">{breakdownSubtitle}</p>
        <ul className="mt-5 space-y-4">
          {composition.map((row) => (
            <li key={row.label}>
              <div className="flex items-center justify-between gap-3 text-xs">
                <span className="min-w-0 font-medium text-umb-neutral-dark">
                  {row.label}
                </span>
                <span className="shrink-0 tabular-nums font-semibold text-evault-muted">
                  {row.pct}%
                </span>
              </div>
              <div className="mt-2 h-8 w-full overflow-hidden rounded-lg bg-evault-surface ring-1 ring-evault-border">
                <div
                  className={`h-full rounded-lg ${row.barClass}`}
                  style={{ width: `${row.pct}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/** Wireframe 6C — full-width recent activity table */
function CertificationReceiptsDashboardBody({ detailColumns, detailRows }) {
  return (
    <div className="p-5 sm:p-6">
      <div className="overflow-hidden rounded-evault border border-evault-border bg-white shadow-sm">
        <div className="border-b border-evault-border bg-evault-surface/50 px-4 py-3 sm:px-5">
          <h3 className="text-sm font-semibold text-umb-neutral-dark">
            Recent certificate activity
          </h3>
          <p className="mt-1 text-[11px] text-evault-muted">
            Sorted by date — receipt / cert identifiers with issuance and delivery
            state.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse text-left text-xs sm:text-sm">
            <thead>
              <tr className="border-b border-evault-border bg-evault-surface/80">
                {detailColumns.map((col) => (
                  <th
                    key={col}
                    className="px-3 py-2.5 font-semibold text-evault-muted"
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {detailRows.map((row, ri) => (
                <tr
                  key={`${ri}-${row.values[0] ?? ri}`}
                  className={`border-b border-evault-border last:border-b-0 ${
                    ri % 2 === 1 ? 'bg-evault-surface/35' : ''
                  }`}
                >
                  {row.values.map((cell, ci) => {
                    const cls = row.valueClasses?.[ci];
                    return (
                      <td
                        key={ci}
                        className={`px-3 py-2.5 text-umb-neutral-dark/90 ${
                          ci === 0 ? 'font-medium text-umb-neutral-dark' : ''
                        } ${cls ?? ''}`.trim()}
                      >
                        {cell}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/** Wireframe 6D — aging bars + longest open items */
function TurnaroundSlaDashboardBody({ slaQueueAging, detailColumns, detailRows }) {
  const maxOverdue = Math.max(
    1,
    ...(slaQueueAging ?? []).map((r) => r.overdue),
  );
  return (
    <div className="grid gap-5 p-5 lg:grid-cols-2 lg:items-stretch lg:gap-6 lg:p-6">
      <div className="rounded-evault border border-evault-border bg-white p-4 shadow-sm sm:p-5">
        <h3 className="text-sm font-semibold text-umb-neutral-dark">
          Aging by queue
        </h3>
        <p className="mt-1 text-xs text-evault-muted">
          Overdue items by custody process — relative bar length vs largest queue.
        </p>
        <ul className="mt-5 space-y-4">
          {(slaQueueAging ?? []).map((row) => {
            const pct = Math.round((row.overdue / maxOverdue) * 100);
            return (
              <li key={row.label}>
                <div className="flex items-center justify-between gap-3 text-xs">
                  <span className="min-w-0 font-medium text-umb-neutral-dark">
                    {row.label}
                  </span>
                  <span className="shrink-0 tabular-nums text-[11px] font-semibold text-evault-muted">
                    {row.overdue} overdue
                  </span>
                </div>
                <div className="mt-2 h-8 w-full overflow-hidden rounded-lg bg-evault-surface ring-1 ring-evault-border">
                  <div
                    className={`h-full rounded-lg ${row.barClass}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="overflow-hidden rounded-evault border border-evault-border bg-white shadow-sm">
        <div className="border-b border-evault-border bg-evault-surface/50 px-4 py-3 sm:px-5">
          <h3 className="text-sm font-semibold text-umb-neutral-dark">
            Longest open items
          </h3>
          <p className="mt-1 text-[11px] text-evault-muted">
            Highest age items and what is blocking progress.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[360px] border-collapse text-left text-xs sm:text-sm">
            <thead>
              <tr className="border-b border-evault-border bg-evault-surface/80">
                {detailColumns.map((col) => (
                  <th
                    key={col}
                    className="px-3 py-2.5 font-semibold text-evault-muted"
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {detailRows.map((row, ri) => (
                <tr
                  key={`${ri}-${row.values[0] ?? ri}`}
                  className={`border-b border-evault-border last:border-b-0 ${
                    ri % 2 === 1 ? 'bg-evault-surface/35' : ''
                  }`}
                >
                  {row.values.map((cell, ci) => {
                    const cls = row.valueClasses?.[ci];
                    return (
                      <td
                        key={ci}
                        className={`px-3 py-2.5 text-umb-neutral-dark/90 ${
                          ci === 0 ? 'font-medium text-umb-neutral-dark' : ''
                        } ${cls ?? ''}`.trim()}
                      >
                        {cell}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function CustodyMovementWireframeBody() {
  return (
    <div className="grid gap-5 p-5 lg:grid-cols-2 lg:items-stretch lg:gap-6 lg:p-6">
      <div className="rounded-evault border border-evault-border bg-evault-surface/30 p-4 sm:p-5">
        <h3 className="text-sm font-semibold text-umb-neutral-dark">
          Movement trend
        </h3>
        <p className="mt-1 text-xs text-evault-muted">
          Total lifecycle actions over the selected period — custody-specific
          volume signal.
        </p>
        <MovementTrendChart />
      </div>
      <div className="rounded-evault border border-evault-border bg-white p-4 shadow-sm sm:p-5">
        <h3 className="text-sm font-semibold text-umb-neutral-dark">
          Movement breakdown
        </h3>
        <p className="mt-1 text-xs text-evault-muted">
          Executed pledges, releases, transfers, and rejected or withdrawn
          actions.
        </p>
        <ul className="mt-5 space-y-4">
          {movementBreakdownRows.map((row) => {
            const pct = Math.round((row.value / movementBreakdownMax) * 100);
            return (
              <li key={row.label}>
                <div className="flex items-center justify-between gap-3 text-xs">
                  <span className="min-w-0 font-medium text-umb-neutral-dark">
                    {row.label}
                  </span>
                  <span className="shrink-0 tabular-nums font-semibold text-umb-neutral-dark">
                    {row.value}
                  </span>
                </div>
                <div className="mt-2 h-8 w-full overflow-hidden rounded-lg bg-evault-surface ring-1 ring-evault-border">
                  <div
                    className={`h-full rounded-lg ${row.barClass}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

const custodyHandoffMatrix = [
  {
    actionType: 'Authority admission',
    ready: 12,
    awaiting: 8,
    overdue: 2,
    executed: 41,
    rejected: 1,
  },
  {
    actionType: 'Pledge',
    ready: 14,
    awaiting: 10,
    overdue: 1,
    executed: 33,
    rejected: 2,
  },
  {
    actionType: 'Release / copy',
    ready: 11,
    awaiting: 6,
    overdue: 0,
    executed: 27,
    rejected: 2,
  },
  {
    actionType: 'Transfer',
    ready: 7,
    awaiting: 3,
    overdue: 1,
    executed: 17,
    rejected: 1,
  },
];

function CustodyHandoffWireframeBody() {
  return (
    <div className="p-5 sm:p-6">
      <div className="overflow-hidden rounded-evault border border-evault-border bg-white shadow-sm">
        <div className="border-b border-evault-border bg-evault-surface/50 px-4 py-3 sm:px-5">
          <h3 className="text-sm font-semibold text-umb-neutral-dark">
            Handoff dashboard
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse text-left text-xs sm:text-sm">
            <thead>
              <tr className="border-b border-evault-border bg-evault-surface/90">
                <th className="px-4 py-3 font-semibold text-evault-muted">
                  Action type
                </th>
                <th className="px-4 py-3 font-semibold text-evault-muted">Ready</th>
                <th className="px-4 py-3 font-semibold text-evault-muted">
                  Awaiting custodian
                </th>
                <th className="px-4 py-3 font-semibold text-evault-muted">Overdue</th>
                <th className="px-4 py-3 font-semibold text-evault-muted">Executed</th>
                <th className="px-4 py-3 font-semibold text-evault-muted">Rejected</th>
              </tr>
            </thead>
            <tbody>
              {custodyHandoffMatrix.map((row, ri) => (
                <tr
                  key={row.actionType}
                  className={`border-b border-evault-border last:border-b-0 ${
                    ri % 2 === 1 ? 'bg-evault-surface/35' : ''
                  }`}
                >
                  <td className="px-4 py-3 font-medium text-umb-neutral-dark">
                    {row.actionType}
                  </td>
                  <td className="px-4 py-3 tabular-nums font-medium text-evault-vault-active">
                    {row.ready}
                  </td>
                  <td className="px-4 py-3 tabular-nums font-medium text-evault-teal-deep">
                    {row.awaiting}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-umb-neutral-dark/90">
                    {row.overdue}
                  </td>
                  <td className="px-4 py-3 tabular-nums font-medium text-evault-navy">
                    {row.executed}
                  </td>
                  <td className="px-4 py-3 tabular-nums font-medium text-red-600">
                    {row.rejected}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function connectorStateClass(state) {
  if (state === 'Healthy') return 'font-semibold text-evault-vault-active';
  if (state === 'Error') return 'font-semibold text-red-600';
  return 'font-semibold text-evault-gold';
}

const integrationConnectorRows = [
  {
    connector: 'Originator API',
    state: 'Healthy',
    lastEvent: '2m ago',
    errorSummary: '—',
    ownerAction: 'Monitor',
  },
  {
    connector: 'Originator webhooks',
    state: 'Error',
    lastEvent: '8m ago',
    errorSummary: '4 5xx deliveries',
    ownerAction: 'Retry / inspect',
  },
  {
    connector: 'DealerX SFTP',
    state: 'Warning',
    lastEvent: '11m ago',
    errorSummary: '8 files waiting parse',
    ownerAction: 'Poller review',
  },
  {
    connector: 'LOS partner feed',
    state: 'Healthy',
    lastEvent: '1m ago',
    errorSummary: '—',
    ownerAction: 'Monitor',
  },
];

function IntegrationHealthWireframeBody() {
  return (
    <div className="p-5 sm:p-6">
      <div className="overflow-hidden rounded-evault border border-evault-border bg-white shadow-sm">
        <div className="border-b border-evault-border bg-evault-surface/50 px-4 py-3 sm:px-5">
          <h3 className="text-sm font-semibold text-umb-neutral-dark">
            Connector detail
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] border-collapse text-left text-xs sm:text-sm">
            <thead>
              <tr className="border-b border-evault-border bg-evault-surface/90">
                <th className="px-4 py-3 font-semibold text-evault-muted">
                  Connector
                </th>
                <th className="px-4 py-3 font-semibold text-evault-muted">State</th>
                <th className="px-4 py-3 font-semibold text-evault-muted">
                  Last event
                </th>
                <th className="px-4 py-3 font-semibold text-evault-muted">
                  Error summary
                </th>
                <th className="px-4 py-3 font-semibold text-evault-muted">
                  Owner action
                </th>
              </tr>
            </thead>
            <tbody>
              {integrationConnectorRows.map((row, ri) => (
                <tr
                  key={row.connector}
                  className={`border-b border-evault-border last:border-b-0 ${
                    ri % 2 === 1 ? 'bg-evault-surface/35' : ''
                  }`}
                >
                  <td className="px-4 py-3 font-medium text-umb-neutral-dark">
                    {row.connector}
                  </td>
                  <td className={`px-4 py-3 ${connectorStateClass(row.state)}`}>
                    {row.state}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-umb-neutral-dark/85">
                    {row.lastEvent}
                  </td>
                  <td className="px-4 py-3 text-umb-neutral-dark/85">
                    {row.errorSummary}
                  </td>
                  <td className="px-4 py-3 text-umb-neutral-dark/90">
                    {row.ownerAction}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="mt-4 text-[11px] leading-relaxed text-evault-muted sm:text-xs">
        Use for connector reliability, retries, failure rates, and latency trends.
      </p>
    </div>
  );
}

function AuthorityOutcomesWireframeBody() {
  return (
    <div className="grid gap-5 p-5 lg:grid-cols-2 lg:items-stretch lg:gap-6 lg:p-6">
      <div className="rounded-evault border border-evault-border bg-evault-surface/30 p-4 sm:p-5">
        <h3 className="text-sm font-semibold text-umb-neutral-dark">
          Outcome mix
        </h3>
        <p className="mt-1 text-xs text-evault-muted">
          Distribution across candidate, repository-only, duplicate-block, and
          items needing provider action before admit / custodian routing.
        </p>
        <div
          className="mt-5 flex h-14 w-full overflow-hidden rounded-lg ring-1 ring-evault-border"
          role="img"
          aria-label="Stacked outcome mix"
        >
          {authorityOutcomeMix.map((seg) => (
            <div
              key={seg.label}
              className={`${seg.barClass} min-w-0 transition-[flex-grow]`}
              style={{ flexGrow: seg.pct }}
              title={`${seg.label}: ${seg.pct}%`}
            />
          ))}
        </div>
        <ul className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-[11px] text-umb-neutral-dark/80">
          {authorityOutcomeMix.map((seg) => (
            <li key={seg.label} className="flex items-center gap-1.5">
              <span
                className={`h-2.5 w-2.5 shrink-0 rounded-sm ${seg.barClass}`}
                aria-hidden
              />
              {seg.label}
            </li>
          ))}
        </ul>
      </div>
      <div className="rounded-evault border border-evault-border bg-white p-4 shadow-sm sm:p-5">
        <h3 className="text-sm font-semibold text-umb-neutral-dark">
          Top authority blockers
        </h3>
        <p className="mt-1 text-xs text-evault-muted">
          Most frequent issues preventing admit or custodian routing.
        </p>
        <ol className="mt-4 list-decimal space-y-2.5 pl-5 text-sm text-umb-neutral-dark/90">
          {authorityBlockers.map((item) => (
            <li key={item} className="leading-snug">
              {item}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

function ReportToolbar() {
  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <button type="button" className={btnRunSchedule}>
        Run
      </button>
      <button type="button" className={btnSchedule}>
        Schedule
      </button>
      <button type="button" className={btnExport}>
        Export
      </button>
    </div>
  );
}

function FullDashboardView({ cfg, reportsCenterPath }) {
  const compositionTitle = cfg.compositionSectionTitle ?? 'Trend / composition';
  const filterGridClass =
    cfg.filterFields.length <= 4
      ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
      : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-5';
  const kpiGridClass =
    cfg.kpis.length <= 4
      ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
      : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-5';

  return (
    <div className="page-stack">
      <section className="rounded-evault border border-evault-border bg-white shadow-sm">
        <div className="flex flex-col gap-4 border-b border-evault-border px-5 py-5 sm:flex-row sm:items-start sm:justify-between sm:px-6 sm:py-6">
          <div className="min-w-0">
            <h2 className="text-lg font-bold tracking-tight text-umb-neutral-dark sm:text-xl">
              {cfg.dashboardTitle}
            </h2>
            <p className="mt-1.5 max-w-2xl text-xs leading-relaxed text-evault-muted sm:text-sm">
              {cfg.dashboardSubtitle}
            </p>
          </div>
          <ReportToolbar />
        </div>

        <div className="border-b border-evault-border bg-evault-surface/50 px-5 py-4 sm:px-6">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
            Filters
          </p>
          <div className={`mt-3 grid gap-3 ${filterGridClass}`}>
            {cfg.filterFields.map((f) => (
              <label key={f.id} className="block min-w-0">
                <span className="text-[11px] font-medium text-umb-neutral-dark/70">
                  {f.label}
                </span>
                <select
                  className={`${filterSelect} mt-1 w-full`}
                  defaultValue={f.options[0]}
                >
                  {f.options.map((o) => (
                    <option key={o}>{o}</option>
                  ))}
                </select>
              </label>
            ))}
          </div>
        </div>

        <div
          className={`grid gap-3 border-b border-evault-border p-5 sm:p-6 ${kpiGridClass}`}
        >
          {cfg.kpis.map((k) => (
            <div
              key={k.label}
              className="rounded-evault border border-evault-border bg-white p-4 shadow-sm"
            >
              <p className="text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
                {k.label}
              </p>
              <p
                className={`mt-2 text-2xl font-bold tabular-nums tracking-tight sm:text-3xl ${k.valueClass}`}
              >
                {k.value}
              </p>
              <p className="mt-2 text-[11px] text-evault-muted">
                {k.hint ?? 'Current filter context'}
              </p>
            </div>
          ))}
        </div>

        {cfg.bodyVariant === 'intake-throughput' ? (
          <IntakeThroughputWireframeBody />
        ) : cfg.bodyVariant === 'authority-outcomes' ? (
          <AuthorityOutcomesWireframeBody />
        ) : cfg.bodyVariant === 'custody-handoff-wireframe' ? (
          <CustodyHandoffWireframeBody />
        ) : cfg.bodyVariant === 'integration-health-wireframe' ? (
          <IntegrationHealthWireframeBody />
        ) : cfg.bodyVariant === 'custody-movement' ? (
          <CustodyMovementWireframeBody />
        ) : cfg.bodyVariant === 'custody-trend-breakdown' ? (
          <CustodyTrendAndBreakdownBody
            trendTitle={
              cfg.dashboardTitle.includes('Inventory')
                ? 'Inventory trend'
                : 'Issuance & delivery trend'
            }
            trendSubtitle={
              cfg.dashboardTitle.includes('Inventory')
                ? 'Assets under custody view across the selected filters.'
                : 'Certificates, receipts, and copy throughput over time (mock series).'
            }
            breakdownTitle={
              cfg.dashboardTitle.includes('Inventory')
                ? 'Custody population mix'
                : 'Artifact lifecycle mix'
            }
            breakdownSubtitle={
              cfg.dashboardTitle.includes('Inventory')
                ? 'Relative mix of custody states in the current portfolio.'
                : 'Share of artifacts by lifecycle stage for the period.'
            }
            composition={cfg.composition}
          />
        ) : cfg.bodyVariant === 'certification-receipts-dashboard' ? (
          <CertificationReceiptsDashboardBody
            detailColumns={cfg.detailColumns}
            detailRows={cfg.detailRows}
          />
        ) : cfg.bodyVariant === 'turnaround-sla-dashboard' ? (
          <TurnaroundSlaDashboardBody
            slaQueueAging={cfg.slaQueueAging}
            detailColumns={cfg.detailColumns}
            detailRows={cfg.detailRows}
          />
        ) : (
          <div className="grid gap-5 p-5 lg:grid-cols-2 lg:items-start lg:gap-6 lg:p-6">
            <div className="rounded-evault border border-evault-border bg-evault-surface/30 p-4 sm:p-5">
              <h3 className="text-sm font-semibold text-umb-neutral-dark">
                {compositionTitle}
              </h3>
              <ul className="mt-4 space-y-3">
                {cfg.composition.map((row) => (
                  <li key={row.label} className="flex items-center gap-3">
                    <span className="w-32 shrink-0 text-xs font-medium text-umb-neutral-dark sm:w-40">
                      {row.label}
                    </span>
                    <div className="h-7 min-w-0 flex-1 overflow-hidden rounded-full bg-white ring-1 ring-evault-border">
                      <div
                        className={`h-full rounded-full ${row.barClass}`}
                        style={{ width: `${row.pct}%` }}
                      />
                    </div>
                    <span className="w-10 shrink-0 text-right text-xs tabular-nums text-evault-muted">
                      {row.pct}%
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="overflow-hidden rounded-evault border border-evault-border bg-white shadow-sm">
              <div className="border-b border-evault-border bg-evault-surface/50 px-4 py-3">
                <h3 className="text-sm font-semibold text-umb-neutral-dark">
                  Detail grid
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[420px] border-collapse text-left text-xs sm:text-sm">
                  <thead>
                    <tr className="border-b border-evault-border bg-evault-surface/80">
                      {cfg.detailColumns.map((col) => (
                        <th
                          key={col}
                          className="px-3 py-2.5 font-semibold text-evault-muted"
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {cfg.detailRows.map((row, ri) => (
                      <tr
                        key={`${ri}-${row.values[0] ?? ri}`}
                        className={`border-b border-evault-border last:border-b-0 ${
                          ri % 2 === 1 ? 'bg-evault-surface/35' : ''
                        }`}
                      >
                        {row.values.map((cell, ci) => {
                          const cls = row.valueClasses?.[ci];
                          return (
                            <td
                              key={ci}
                              className={`px-3 py-2.5 text-umb-neutral-dark/90 ${
                                ci === 0 ? 'font-medium text-umb-neutral-dark' : ''
                              } ${cls ?? ''}`.trim()}
                            >
                              {cell}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </section>

      <Link
        to={reportsCenterPath}
        className="inline-flex text-xs font-semibold text-evault-teal hover:underline sm:text-sm"
      >
        ← Back to Reports center
      </Link>
    </div>
  );
}

export function ReportDashboardPage() {
  const { pathname } = useLocation();
  const cfg = useMemo(() => ALL_REPORT_CONFIG[pathname], [pathname]);
  const reportsCenterPath = pathname.startsWith('/provider')
    ? '/provider/reports'
    : pathname.startsWith('/custodian')
      ? '/custodian/reports'
      : '/reports';

  if (!cfg) {
    return (
      <p className="text-sm text-umb-error">
        Unknown report.{' '}
        <Link to={reportsCenterPath} className="font-medium underline">
          Back to reports
        </Link>
      </p>
    );
  }

  return (
    <FullDashboardView cfg={cfg} reportsCenterPath={reportsCenterPath} />
  );
}
